import React from 'react';
import { ProcLocalParams, ProcOptionItem, ProcOptions } from '../../api/assets';
import SearchSelect from '../../components/SearchSelect';
import { useDictText, useProcessTypeText } from '../../utils/dictOptions';
import { useMModeText } from '../../utils/mmodeText';
import { zhOptions } from '../../utils/searchOptions';
import { SegGroup } from './parts';
import { restoreText } from './processHistoryLabels';
import { satReasonText } from './procLocalText';

// 材料加工 · 读本库后新增的筛选 (2026-10-20): 设备模组 / 加工类型 / 模式 / 光源 / 功率速度区间 /
// 打标来源 / 置信度 / 场景可评 / 加工前图 / 不满意原因, 以及只对历史数据有意义的元素类型与耗材还原三项.
// 下拉选项来自 /v1/assets/process-records/options (带条数), 展示名走字典 / 既有词表, 不另抄常量.
//
// 2026-10-21 筛选区改版: 不再整块平铺, 按主题拆成几段 (ProcLocalSection), 由 ProcessList 摆进
// 「常用 / 更多筛选」两层的分组里; 分组外框与栅格 (FilterGroup / FilterGrid) 两边共用.

type Key = keyof ProcLocalParams;

/** 分组: 左侧小标题 + 右侧内容 (分段按钮一行, 下拉/输入走等宽栅格). */
export function FilterGroup({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div className="grid gap-x-3 gap-y-2 items-start" style={{ gridTemplateColumns: '64px minmax(0, 1fr)' }}>
            <div className="text-[11px] font-semibold text-slate-400 pt-1.5">{title}</div>
            <div className="space-y-2 min-w-0">{children}</div>
        </div>
    );
}

/** 等宽栅格: 下拉与输入框统一列宽, 窄屏自动折行, 不出横向滚动. */
export function FilterGrid({ children }: { children: React.ReactNode }) {
    return (
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))' }}>
            {children}
        </div>
    );
}

/** 分段按钮一行 (折行时上下间距一致). */
export function SegRow({ children }: { children: React.ReactNode }) {
    return <div className="flex flex-wrap items-center gap-x-5 gap-y-2">{children}</div>;
}

/** 数值区间 (功率 / 速度): 两个输入 + 中间的 ~ 作为一个整体, 不会被拆到两行. */
function RangeInput({ label, unit, lo, hi, onLo, onHi }: {
    label: string; unit: string; lo: string; hi: string; onLo: (v: string) => void; onHi: (v: string) => void;
}) {
    const clean = (v: string) => v.replace(/[^0-9.]/g, '');
    return (
        <div className="flex items-center gap-1.5 min-w-0" title={`${label} (${unit})`}>
            <span className="text-xs text-slate-400 shrink-0">{label}</span>
            <input className="inp min-w-0 flex-1" inputMode="decimal" placeholder="最小" value={lo}
                onChange={(e) => onLo(clean(e.target.value))} />
            <span className="text-slate-300 shrink-0">~</span>
            <input className="inp min-w-0 flex-1" inputMode="decimal" placeholder="最大" value={hi}
                onChange={(e) => onHi(clean(e.target.value))} />
        </div>
    );
}

function withCount(items: ProcOptionItem[] | undefined, text: (v: string) => string) {
    return zhOptions(items || [], (i) => i.value, (i) => `${text(i.value)} · ${i.count.toLocaleString()}`);
}

/**
 * 读本库筛选的一段:
 *   module  设备模组 (常用层);
 *   effect  置信度 + 不满意原因;
 *   scene   场景可评 + 加工前图 (分段);
 *   params  加工类型 / 模式 / 光源 + 功率 / 速度区间;
 *   history 仅历史数据的元素类型与耗材还原三项 (数据来源=Studio 实时时由调用方不渲染).
 */
export default function ProcLocalSection({
    section,
    value,
    onChange,
    options,
}: {
    section: 'module' | 'effect' | 'scene' | 'params' | 'history';
    value: ProcLocalParams;
    onChange: (k: Key, v: string) => void;
    options: ProcOptions | null;
}) {
    const ptText = useProcessTypeText();
    const modeText = useMModeText();
    const lightText = useDictText('light_source');
    const v = (k: Key) => (value[k] as string | undefined) || '';
    const select = (k: Key, placeholder: string, items: ProcOptionItem[] | undefined, text: (x: string) => string, title?: string) => (
        <span key={k} title={title} className="min-w-0">
            <SearchSelect className="w-full" value={v(k)} onChange={(x) => onChange(k, x)}
                placeholder={placeholder} clearable options={withCount(items, text)} />
        </span>
    );
    switch (section) {
        case 'module':
            return select('device_module', '全部设备模组', options?.device_modules, (x) => x, '设备模组 = 机型 × 激光模组 (光源 / 瓦数)');
        case 'effect':
            return (
                <>
                    <SegRow>
                        <SegGroup label="置信度" value={v('low_conf')} onChange={(x) => onChange('low_conf', x)}
                            title="胜出效果标签的置信度; 低于 0.4 的不进效果分档"
                            options={[{ value: '', label: '全部' }, { value: 'false', label: '可判读' }, { value: 'true', label: '低于 0.4' }]} />
                    </SegRow>
                    <FilterGrid>
                        {select('sat_reason', '全部不满意原因', options?.sat_reasons, satReasonText)}
                    </FilterGrid>
                </>
            );
        case 'scene':
            return (
                <SegRow>
                    <SegGroup label="场景可评" value={v('scene_state')} onChange={(x) => onChange('scene_state', x)}
                        options={[{ value: '', label: '全部' }, { value: 'evaluable', label: '可评估' }, { value: 'unevaluable', label: '不可评估' }, { value: 'none', label: '未打标' }]} />
                    <SegGroup label="加工前图" value={v('before_kind')} onChange={(x) => onChange('before_kind', x)}
                        options={[{ value: '', label: '全部' }, { value: 'photo', label: '实拍' }, { value: 'restored', label: '模型还原' }, { value: 'none', label: '无' }]} />
                </SegRow>
            );
        case 'params':
            return (
                <FilterGrid>
                    {select('processing_type', '全部加工类型', options?.processing_types, (x) => ptText(x))}
                    {select('processing_mode', '全部加工模式', options?.processing_modes, (x) => modeText(x))}
                    {select('light_source', '全部光源', options?.light_sources, (x) => lightText(x))}
                    <RangeInput label="功率%" unit="%" lo={v('power_min')} hi={v('power_max')}
                        onLo={(x) => onChange('power_min', x)} onHi={(x) => onChange('power_max', x)} />
                    <RangeInput label="速度" unit="mm/s" lo={v('speed_min')} hi={v('speed_max')}
                        onLo={(x) => onChange('speed_min', x)} onHi={(x) => onChange('speed_max', x)} />
                </FilterGrid>
            );
        case 'history':
            return (
                <FilterGrid>
                    {select('element_type', '全部元素类型', options?.element_types, (x) => x)}
                    {select('restore_qc', '还原质检不限', options?.restore_qc, (x) => restoreText('qc_verdict', x))}
                    {select('restore_finish', '还原表面不限', options?.restore_finish, (x) => restoreText('restore_finish', x))}
                    {select('restore_label_cat', '还原标注不限', options?.restore_label_cat, (x) => restoreText('restore_label', x))}
                </FilterGrid>
            );
        default:
            return null;
    }
}
